<template>
  <div class="search-network">
    <div class="search-network__blob search-network__blob--pink" aria-hidden="true"></div>
    <div class="search-network__blob search-network__blob--orange" aria-hidden="true"></div>

    <div class="search-network__inner">
    <p class="search-network__eyebrow">Discover. Connect. Collaborate.</p>

      <h1 class="search-network__title">Find who you need.</h1>

      <p class="search-network__subtitle">
        Explore your industry and connect with the right businesses and service providers.
      </p>

      <div class="search-card">
        <div class="type-tabs">
          <button
            v-for="cat in categories"
            :key="cat.value"
            type="button"
            class="type-tab"
            :class="{ 'is-active': selectedType === cat.value }"
            @click="selectType(cat.value)"
          >
            <span class="type-tab__icon" v-html="icons[cat.value]"></span>
            <span class="type-tab__text">
              <span class="type-tab__label">{{ cat.label }}</span>
              <span class="type-tab__desc">{{ cat.desc }}</span>
            </span>
          </button>
        </div>

        <transition name="search-row">
          <div v-if="selectedType" class="search-row">
            <span class="search-row__icon" v-html="icons.search"></span>
            <input
              ref="keywordInput"
              v-model="keyword"
              type="text"
              class="search-row__input"
              :placeholder="placeholders[selectedType]"
              @input="onKeywordInput"
            />
            <button type="button" class="search-row__submit" @click="emitSearch">
              Search
            </button>
          </div>
        </transition>

        <p v-if="!selectedType" class="search-card__hint">Pick a category to start searching.</p>
      </div>
    </div>

    <!-- ============ Results ============ -->
    <div v-if="selectedType" class="results">
      <div class="results__inner">
        <div class="results__head">
          <span class="results__count">
            <template v-if="loading">Searching…</template>
            <template v-else>{{ pagination.total_rows }} result{{ pagination.total_rows === 1 ? '' : 's' }}</template>
            <template v-if="keyword"> for "{{ keyword }}"</template>
          </span>
        </div>
        <div v-if="loading" class="results-grid">
          <div v-for="n in 6" :key="n" class="result-tile result-tile--skeleton">
            <div class="result-tile__top">
              <div class="result-tile__logo result-tile__logo--skeleton"></div>
              <div class="result-tile__body">
                <div class="result-tile__skeleton-line result-tile__skeleton-line--wide"></div>
                <div class="result-tile__skeleton-line"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="!suppliers.length" class="results-empty">
          <span class="results-empty__icon" v-html="icons[selectedType]"></span>
          <p class="results-empty__title">No {{ categoryLabel.toLowerCase() }} found</p>
          <p class="results-empty__desc">Try a different keyword or switch category.</p>
        </div>

        <!-- Result tiles -->
        <div v-else class="results-grid">
          <router-link
            v-for="supplier in suppliers"
            :key="supplier.id"
            :to="{ name: 'SupplierProfile', params: { external_id: supplier.external_id } }"
            class="result-tile"
          >
            <div class="result-tile__top">
              <div class="result-tile__logo">
                <img v-if="supplier.logo" :src="getFileUrl(supplier.logo)" :alt="supplier.company_name" />
                <span v-else class="result-tile__logo-fallback"><i class="fas fa-truck-loading"></i></span>
              </div>

              <div class="result-tile__body">
                <div class="result-tile__row">
                  <span class="result-tile__name">{{ supplier.company_name }}</span>
                        <span class="result-card__since">Member since {{ supplier.created_at }}</span>
                  </div>
                  <span class="result-tile__location">
                    {{ [supplier.street_city, supplier.state].filter(Boolean).join(', ') || '—' }}
                  </span>
                </div>
             </div>

            <div v-if="matchedItems(supplier).length" class="result-tile__footer">
              <span v-for="(item, i) in matchedItems(supplier).slice(0, 4)" :key="i" class="tile-badge">
                {{ item.name }}
              </span>
              <span v-if="matchedItems(supplier).length > 4" class="tile-badge tile-badge--more">
                +{{ matchedItems(supplier).length - 4 }}
              </span>
            </div>
          </router-link>
        </div>
        <div v-if="!loading && pagination.total_pages > 1" class="results-pagination">
          <button
            type="button"
            class="results-pagination__btn"
            :disabled="pagination.current_page <= 1"
            @click="changePage(pagination.current_page - 1)"
          >
            ‹ Prev
          </button>
          <span class="results-pagination__label">
            Page {{ pagination.current_page }} of {{ pagination.total_pages }}
          </span>
          <button
            type="button"
            class="results-pagination__btn"
            :disabled="pagination.current_page >= pagination.total_pages"
            @click="changePage(pagination.current_page + 1)"
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { form_api_submit } from '@/utils/FormSubmit'

export default {
  name: 'SearchNetwork',

  emits: ['search'],

  data() {
    return {
      selectedType: null,
      keyword: '',
      debounceTimer: null,
      loading: false,
      suppliers: [],
      pagination: {
        current_page: 1,
        total_rows: 0,
        total_pages: 1,
        per_page: 20,
      },

      categories: [
        { value: 'supplier', label: 'Suppliers', desc: 'Raw materials & ingredients' },
        { value: 'manufacturer', label: 'Manufacturers', desc: 'Finished goods & production' },
        { value: 'service_provider', label: 'Service Providers', desc: 'Logistics, QA & contractors' },
      ],

      placeholders: {
        supplier: 'Search suppliers...',
        manufacturer: 'Search manufacturers...',
        service_provider: 'Search service providers or contractors...',
      },

      icons: {
        company:
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21V6a1 1 0 0 1 1-1h6v16"/><path d="M15 21V10a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v11"/><path d="M8 8h0M8 11h0M8 14h0M8 17h0"/><path d="M4 21h16"/></svg>',
        supplier:
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8l-9-5-9 5 9 5 9-5z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg>',
        manufacturer:
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21V11l6 4v-4l6 4v-4l6 4v6H3z"/><path d="M7 21v-4"/><path d="M13 21v-4"/><circle cx="7" cy="6" r="1.4"/><circle cx="13" cy="4" r="1.4"/></svg>',
        service_provider:
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M8 12l2.5 2.5L16 9"/><path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 12h4"/></svg>',
        search:
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>',
      },
    }
  },

  computed: {
    categoryLabel() {
      const cat = this.categories.find((c) => c.value === this.selectedType)
      return cat ? cat.label : ''
    },

    // Build query params for API
    queryParams() {
      const params = new URLSearchParams()

      if (this.selectedType) {
        params.append('type', this.selectedType)
      }

      if (this.keyword.trim()) {
        params.append('keyword', this.keyword.trim())
      }

      if (this.pagination.current_page > 1) {
        params.append('page', this.pagination.current_page)
      }

      return params.toString()
    },

    // Full URL with query string
    searchUrl() {
      const base = '/search'
      const params = this.queryParams
      return params ? `${base}?${params}` : base
    },
  },

  watch: {
    // Update URL when filters change
    queryParams(newParams, oldParams) {
      if (newParams !== oldParams) {
        const url = this.searchUrl
        window.history.pushState({}, '', url)
      }
    },
  },

  mounted() {
    // Parse URL params on load
    this.parseUrlParams()
    // Initial load
    if (this.selectedType) this.fetchList()
  },

  beforeUnmount() {
    clearTimeout(this.debounceTimer)
  },

  methods: {
    getFileUrl(filePath) {
      if (!filePath) return ''
      if (filePath.startsWith('http')) return filePath
      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      return `${baseUrl}/${filePath}`
    },
    matchedItems(supplier) {
      return supplier.matched_materials || supplier.matched_products || supplier.matched_services || []
    },

    // Parse URL parameters on page load
    parseUrlParams() {
      const params = new URLSearchParams(window.location.search)

      const type = params.get('type')
      if (type && this.categories.some((c) => c.value === type)) {
        this.selectedType = type
      }

      const keyword = params.get('keyword')
      if (keyword) {
        this.keyword = keyword
      }

      const page = params.get('page')
      if (page && !isNaN(page)) {
        this.pagination.current_page = parseInt(page)
      }
    },

    selectType(type) {
      this.selectedType = this.selectedType === type ? null : type
      this.keyword = ''
      this.pagination.current_page = 1
      if (this.selectedType) {
        this.fetchList()
        this.$nextTick(() => this.$refs.keywordInput?.focus())
      } else {
        this.suppliers = []
      }
    },

    onKeywordInput() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.pagination.current_page = 1
        this.fetchList()
      }, 300)
    },

    emitSearch() {
      clearTimeout(this.debounceTimer)
      this.pagination.current_page = 1
      this.fetchList()
    },

    async fetchList() {
      this.loading = true

      try {
        // Build query string for API
        const params = new URLSearchParams()

        if (this.selectedType) {
          params.append('type', this.selectedType)
        }

        if (this.keyword.trim()) {
          params.append('keyword', this.keyword.trim())
        }

        if (this.pagination.current_page > 1) {
          params.append('page', this.pagination.current_page)
        }

        const queryString = params.toString()
        const url = `search/${queryString ? '?' + queryString : ''}`

        const result = await form_api_submit({
          url: url,
          method: 'GET',
          show_success_toast: false,
          show_error_toast: true,
          remove_pre_toasts: false,
        })

        if (result.success) {
          // Actual response shape: { data: { type, keyword, results: { list, pagination } } }
          const payload = result.data || {}
          const results = payload.results || {}
          const rows = Array.isArray(results.list) ? results.list : []

          this.suppliers = rows.map((s) => ({
            ...s,
            _dropdownOpen: false,
            status: this.normalizeStatus(s.approval_status),
          }))

          if (results.pagination) {
            this.pagination = { ...this.pagination, ...results.pagination }
          } else {
            this.pagination.total_rows = this.suppliers.length
            this.pagination.total_pages = 1
          }

          this.$emit('search', {
            results: this.suppliers,
            pagination: this.pagination,
            filters: {
              type: this.selectedType,
              keyword: this.keyword,
            },
          })
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error)
      } finally {
        this.loading = false
      }
    },

    normalizeStatus(status) {
      const statusMap = {
        approved: 'approved',
        pending: 'pending',
        rejected: 'rejected',
        draft: 'draft',
        active: 'approved',
        inactive: 'pending',
      }
      return statusMap[status?.toLowerCase()] || 'pending'
    },

    // Pagination methods
    loadMore() {
      if (this.pagination.current_page < this.pagination.total_pages) {
        this.pagination.current_page++
        this.fetchList()
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.pagination.total_pages) {
        this.pagination.current_page = page
        this.fetchList()
      }
    },
  },
}
</script>
<style scoped>
.search-network {
  position: relative;
  min-height: 640px;
  overflow: hidden;
  padding: 72px 20px 90px;
  background: linear-gradient(180deg, var(--color-tint-light) 0%, #f8f0f4 55%, #fdf1f6 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.search-network * {
  box-sizing: border-box;
}

.search-network__blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  pointer-events: none;
}
.search-network__blob--pink {
  width: 520px;
  height: 420px;
  left: 8%;
  top: 18%;
  background: radial-gradient(circle, var(--color-tint-warm) 0%, transparent 72%);
}
.search-network__blob--orange {
  width: 480px;
  height: 380px;
  right: 6%;
  bottom: 4%;
  background: radial-gradient(circle, var(--color-tint-warm) 0%, transparent 72%);
}

.search-network__inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 760px;
  text-align: center;
}

.search-network__eyebrow {
  margin: 0 0 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-secondary);
}

.search-network__title {
  margin: 0 0 12px;
  font-size: clamp(30px, 5vw, 42px);
  font-weight: 800;
  line-height: 1.5;
  letter-spacing: -0.01em;
  background: linear-gradient(100deg, var(--color-primary), var(--color-secondary) 65%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.search-network__subtitle {
  margin: 0 0 40px;
  font-size: 15px;
  color: var(--color-text-muted);
}

/* ---------- Card ---------- */
.search-card {
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(22px) saturate(180%);
  -webkit-backdrop-filter: blur(22px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 26px;
  padding: 22px;
  box-shadow: 0 30px 60px -24px rgba(12, 18, 83, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.7);
  text-align: left;
}

.type-tabs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.type-tab {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 16px;
  border: 1.5px solid transparent;
  background: rgba(12, 18, 83, 0.03);
  cursor: pointer;
  text-align: left;
  transition: transform 0.16s ease, background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
}
.type-tab:hover {
  transform: translateY(-2px);
  background: rgba(12, 18, 83, 0.055);
}
.type-tab__icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  transition: color 0.16s ease, background 0.16s ease, border-color 0.16s ease;
}
.type-tab__icon svg {
  width: 18px;
  height: 18px;
}
.type-tab__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.type-tab__label {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}
.type-tab__desc {
  font-size: 11px;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.type-tab.is-active {
  background: linear-gradient(135deg, rgba(236, 153, 58, 0.09), rgba(11, 23, 153, 0.09));
  border-color: rgba(11, 23, 153, 0.35);
  box-shadow: 0 10px 24px -14px rgba(11, 23, 153, 0.45);
}
.type-tab.is-active .type-tab__icon {
  background: linear-gradient(135deg, var(--color-tint-warm), var(--color-secondary));
  color: #fff;
  border-color: transparent;
}

/* ---------- Search row ---------- */
.search-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 6px 6px 6px 16px;
  border-radius: 16px;
  background: var(--color-surface);
  border: 1.5px solid var(--color-border);
}
.search-row:focus-within {
  border-color: rgba(11, 23, 153, 0.4);
  box-shadow: 0 0 0 4px rgba(11, 23, 153, 0.1);
}
.search-row__icon {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  color: var(--color-text-muted);
}
.search-row__icon svg {
  width: 17px;
  height: 17px;
}
.search-row__input {
  flex: 1;
  min-width: 0;
  height: 46px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-dark);
}
.search-row__input::placeholder {
  color: var(--color-text-muted);
  font-weight: 400;
}
.search-row__submit {
  flex-shrink: 0;
  height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: box-shadow 0.16s ease, transform 0.15s ease;
  box-shadow: 0 10px 22px -10px rgba(11, 23, 153, 0.55);
}
.search-row__submit:hover {
  box-shadow: 0 12px 26px -8px rgba(11, 23, 153, 0.65);
}
.search-row__submit:active {
  transform: scale(0.96);
}

.search-card__hint {
  margin: 14px 2px 2px;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

/* Search row enter/leave */
.search-row-enter-active {
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1), margin-top 0.22s ease;
}
.search-row-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease, margin-top 0.15s ease;
}
.search-row-enter-from,
.search-row-leave-to {
  opacity: 0;
  transform: translateY(-6px);
  margin-top: 0;
}

/* ==========================================================================
   Results
   ========================================================================== */
.results {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1080px;
  margin-top: 40px;
}

.results__inner {
  width: 100%;
}

.results__head {
  margin-bottom: 16px;
  padding: 0 4px;
}

.results__count {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-muted);
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

/* ---------- Result tile — larger, static, no motion/shadow ---------- */
.result-tile {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 14px 16px;
  text-decoration: none;
}

.result-tile__top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.result-tile__logo {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--color-tint-light);
  border: 1px solid var(--color-border);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.result-tile__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.result-tile__logo-fallback {
  display: grid;
  place-items: center;
  color: var(--color-secondary);
}
.result-tile__logo-fallback svg {
  width: 19px;
  height: 19px;
}

.result-tile__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.result-tile__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.result-tile__name {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--color-text-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-tile__status {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 3px 8px;
  border-radius: 999px;
}
.result-tile__status--approved {
  background: rgba(26, 127, 75, 0.1);
  color: #1a7f4b;
}
.result-tile__status--pending {
  background: rgba(236, 153, 58, 0.16);
  color: #a15b0a;
}
.result-tile__status--rejected {
  background: rgba(179, 38, 30, 0.1);
  color: #b3261e;
}
.result-tile__status--draft {
  background: var(--color-tint-light);
  color: var(--color-text-muted);
}

.result-tile__location {
  font-size: 12px;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-tile__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid var(--color-border);
}

.tile-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  background: var(--color-tint-light);
  color: var(--color-text-dark);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.tile-badge--more {
  color: var(--color-text-muted);
}

/* ---------- Skeleton loading ---------- */
.result-tile--skeleton {
  gap: 0;
}
.result-tile--skeleton .result-tile__top {
  width: 100%;
}
.result-tile__logo--skeleton {
  background: linear-gradient(90deg, var(--color-border) 25%, var(--color-tint-light) 50%, var(--color-border) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}
.result-tile__skeleton-line {
  height: 9px;
  border-radius: 5px;
  background: linear-gradient(90deg, var(--color-border) 25%, var(--color-tint-light) 50%, var(--color-border) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  width: 55%;
}
.result-tile__skeleton-line--wide {
  width: 80%;
  margin-bottom: 6px;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ---------- Empty state ---------- */
.results-empty {
  text-align: center;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px dashed var(--color-border);
  border-radius: 20px;
}
.results-empty__icon {
  display: inline-grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: var(--color-tint-light);
  color: var(--color-text-muted);
  margin-bottom: 14px;
}
.results-empty__icon svg {
  width: 20px;
  height: 20px;
}
.results-empty__title {
  margin: 0 0 4px;
  font-size: 14.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}
.results-empty__desc {
  margin: 0;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

/* ---------- Pagination ---------- */
.results-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 28px;
}
.results-pagination__btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-dark);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease;
}
.results-pagination__btn:hover:not(:disabled) {
  background: var(--color-tint-light);
  border-color: var(--color-secondary);
}
.results-pagination__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.results-pagination__label {
  font-size: 12.5px;
  color: var(--color-text-muted);
  font-weight: 500;
}

/* ---------- Responsive ---------- */
@media (max-width: 720px) {
  .type-tabs {
    grid-template-columns: 1fr;
  }
  .type-tab__desc {
    white-space: normal;
  }
  .results-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .search-network {
    padding: 56px 16px 64px;
  }
  .search-card {
    padding: 16px;
    border-radius: 20px;
  }
  .search-row {
    flex-wrap: wrap;
    padding: 10px 12px;
  }
  .search-row__input {
    height: 40px;
  }
  .search-row__submit {
    width: 100%;
  }
}
</style>